import { AxiosInstance } from '../../../../utilities/requestClient';
import { BASE_URL } from '../../../../utilities/apiConstants';
import RaptorLoading from '../../../feedback/RaptorLoading';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { Button, Grid } from '@mui/material';
import {
  PortalUpdatePosition,
  buildPortalColumns,
  buildPortalData,
  portalUpdateColumns,
  updateColumnTitles,
} from './utils/buildFns';
import UltraTable, { TableData } from '../../../tables/ultraTable/UltraTable';
import {
  ColumnData,
  ColumnFields,
} from '../../../tables/ultraTable/types/column.types';
import { toast } from '../../../toast/Toast';
import { UltraTablePreset } from '../../../tables/ultraTable/types/presets.types';
import { useEffect, useState } from 'react';
import useConfirmation from '../../../../hooks/useConfirmation';
import {
  AuthorisedFundConfigType,
  AuthorisedKey,
  DefaultColumn,
  PositionUpdatePostData,
} from './PositionsUpdatePortal.types';
import { formatDateForCheckingState } from '../../../../utilities/dateFormatters';
import PortalWarning from './components/PortalWarning';
import { PortalFetchWrapper } from './PortalFetchWrapper';
import { useEditablePositionsData } from './services/queries';
import { isEmptyObject } from '../../../../utilities/helpers/common/objects';
import GenericModal from '../../../ui/GenericModal';
import { GenericPortalUpdateForm } from '../../../tables/ultraTable/components/EditableCell';
import { EditableFieldsProperties } from '../../../tables/ultraTable/types/editable.types';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';

export const NESTED_KEY_REGEX = /---x3A\/8n2\/G3c\/w5L---/g;

function transformAuthorisedKeys(
  authorisedKeys: AuthorisedKey[],
): (Omit<AuthorisedKey, 'key_path_name'> & { key_name: string })[] {
  return authorisedKeys.map(({ key_path_name, ...rest }) => ({
    ...rest,
    key_name: key_path_name, // Rename key_path_name to key_name
  }));
}

interface ExtendedFundInfoComponentProps extends FundInfoComponentProps {
  authConfig?: AuthorisedFundConfigType; // This is optional as I dont know how to tell typescript that the data will be there as it is handled in the FetchPortalWrapper
}

const PositionUpdatePortalComponent: React.FC<
  ExtendedFundInfoComponentProps
> = (props) => {
  const { handleConfirmation } = useConfirmation();

  const [changesPending, setChangesPending] = useState(false);
  const [isDataEqual, setIsDataEqual] = useState(true);
  const [showEditableForm, setShowEditableForm] = useState(false);
  const [editForm, setEditForm] = useState<JSX.Element | null>(null);

  const handleDataComparison = (isEqual: boolean) => {
    setIsDataEqual(isEqual);
  };

  const fundId = props?.fundId;

  const { data, isPending, error, isFetching } = useEditablePositionsData(
    fundId,
    props.authConfig,
    {
      staleTime: 30 * 60 * 1000,
    },
  );

  const positionDate = data?.returned_position_date || '';

  // Handles the beforeunload event to prompt the user if they want to leave the page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // We are not handling on updateAttempted as we cannot change the browser message and as such dont want to confuse the user
      if (!isDataEqual) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDataEqual]);

  useEffect(() => {
    const updatedColumns = data?.positions.some((position) => {
      return (
        position?.cdum_changed_paths && position?.cdum_changed_paths.length > 0
      );
    });

    setChangesPending(updatedColumns || false);
  }, [data]);
  if (isPending) {
    return (
      <RaptorLoading
        centerWrap
        messages={[
          'Loading Position Update Portal...',
          'This may take a few seconds...',
        ]}
      />
    );
  }

  if (error) {
    return <NoDataMessage message={'Error Retrieving Data'} />;
  }

  if (!data) {
    return <NoDataMessage message={'No Data Available'} />;
  }

  const { authorised_keys, default_columns } = props.authConfig!; // I know this will be seeded as errors are handled in the wrapper

  // This transforms the key_name_path to key_name as key_name is used everywhere in frontend
  const transformedAuthKeyNameArr = transformAuthorisedKeys(authorised_keys);

  const hasFunds = isEmptyObject(transformedAuthKeyNameArr);

  const mutatedColumns = MutateColumnsToIncludeEditableFields(
    portalUpdateColumns,
    transformedAuthKeyNameArr,
  );

  const originalData = {
    data,
    columns: hasFunds ? mutatedColumns : portalUpdateColumns,
  };

  const mutatedData = buildPortalData(originalData.data);

  const handleUpdate = async (
    originalRow: PortalUpdatePosition,
    columnId: any,
    columnName: any,
    valueToUpdate: any,
    updatePositionData: any,
  ) => {
    if (!fundId) {
      toast({
        title: 'Error updating field',
        message: `No Fund Id or Position Date`,
        type: 'error',
      });
      return;
    }



    // Create the FormData object to send to the backend
    const formattedKey = columnId.replace(NESTED_KEY_REGEX, '.');
    // Data in this
    const data: PositionUpdatePostData = {
      fund_name: fundId, // Currently this is the fund_name - but should be the fund_id
      selected_position_date: positionDate,
      position_id: originalRow.position_id,
      targeted_position_key_path: formattedKey,
      targeted_position_value: valueToUpdate,
    };

    // Add audit log data to the request payload
    const requestData = {
      ...data,
      rs_raptor_info: {
        page_id: '550e8400-e29b-41d4-a716-446655440000', // Temporary UUID until configs DB setup
        app_name: 'Raptor',
        page_name: 'Position Update Portal',
      },
    };

    try {
      await updatePositionData.mutateAsync(requestData);
    } catch (error) {
      toast({
        title: `${columnName} - Update Failed`,
        message: (
          <span className="flex flex-col">
            <div className="flex">
              <div className="w-[40px] font-medium">Asset:</div>
              <div className="flex-1 break-words italic">
                {originalRow.gm_name_long}
              </div>
            </div>
            <div className="flex">
              <div className="w-[40px] font-medium">Fund:</div>
              <div className="flex-1 break-words italic">
                {originalRow.fund_name}
              </div>
            </div>
          </span>
        ),
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleRecalculation = async () => {
    if (!fundId) {
      toast({
        title: 'Error finding Fund',
        message: `No Fund Id available`,
        type: 'error',
      });
      return;
    }

    try {
      await AxiosInstance.get(`${BASE_URL}request_recalculation/${fundId}`, {
        withCredentials: true,
      });

      toast({
        title: 'Recalculation Requested',
        message: `Recalculation requested for ${fundId}, please be patient`,
        type: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error requesting recalculation',
        message: `Error requesting recalculation: ${error}`,
        type: 'error',
      });
    }
  };

  const columnTitlesInfo: DefaultColumn[] = default_columns.map((col) => {
    return {
      column_name: col.column_name,
      column_key: col.column_key,
    };
  });

  const authorisedKeysInfo: DefaultColumn[] = transformedAuthKeyNameArr.map(
    (key) => {
      return {
        column_name: key.column_name,
        column_key: key.key_name,
      };
    },
  );

  const combinedColumnTitlesInfo: DefaultColumn[] = [
    ...columnTitlesInfo,
    ...authorisedKeysInfo,
  ];

  // Remove duplicates based on column_key
  const uniqueColumnTitlesInfo: DefaultColumn[] = Array.from(
    new Map(
      combinedColumnTitlesInfo.map((item) => [item.column_key, item]),
    ).values(),
  );

  const columns = buildPortalColumns(
    updateColumnTitles(uniqueColumnTitlesInfo, originalData.columns),
    renderForm,
  );

  const tableData: TableData<PortalUpdatePosition> = {
    data: mutatedData,
    columns,
  };

  const presets = createHedgeFundPresets(
    transformedAuthKeyNameArr,
    default_columns,
  ); // The auth keys are the presets in this instance

  const onConfirmation = () => {
    handleConfirmation(
      'Recalculation Request',
      <>
        <strong>
          Are you sure you want to alert Risksystem to recalculate the fund?
        </strong>
        <p>
          Please make sure you have submitted all changes before requesting a
          recalculation.
        </p>
      </>,
      () => {
        handleRecalculation();
      },
    );
  };

  const RequestRecalculationBtn = () => {
    return (
      <Button
        onClick={onConfirmation}
        disabled={!changesPending}
        variant="contained"
      >
        Request Recalculation
      </Button>
    );
  };

  // This is for always having non filterable columns
  // TODO: a better solution will be setting the ColumnDef to not filterable in tanstack
  const exclusions = [
    'index',
    ...default_columns.map((col) => col?.column_key),
  ];

  function renderForm(
    value: any,
    editOptions: EditableFieldsProperties,
    original: PortalUpdatePosition,
    id: string,
  ) {
    setShowEditableForm(true);
    setEditForm(
      <GenericPortalUpdateForm
        value={value}
        editOptions={editOptions}
        callBackEditFn={handleUpdate}
        original={original}
        id={id}
        onClose={() => setShowEditableForm(false)}
      />,
    );
  }

  return (
    <Grid container spacing={2} style={{ padding: 8 }}>
      <GenericModal
        isOpen={showEditableForm}
        setIsOpen={setShowEditableForm}
        className="min-w-fit"
        title={'Edit value'}
      >
        {editForm}
      </GenericModal>

      <PortalWarning />

      <UltraTable<PortalUpdatePosition>
        title={`Position Update Portal - ${positionDate}`}
        tableData={tableData}
        isFetching={isFetching}
        getRowCanExpand={() => true}
        exclusions={exclusions}
        columnSelector
        toolBarBtns={[<RequestRecalculationBtn />]}
        {...(presets &&
          presets.length > 0 && {
          presets: {
            default: presets[0],
            allPresets: presets,
            display: 'dropdown',
          },
        })}
        isEditable
        onDataComparison={handleDataComparison}
        positionDate={positionDate}
        pdfConfig={{
          pdfIdentifier: `${fundId}_position_portal`,
          pdfTitle: `Portal Data - ${fundId}`,
          pdfExportGroupName: 'portal_page',
          pdfExportGroupOrder: 5,
          pdfFileName: `exposure-${fundId}-${positionDate || formatDateForCheckingState(new Date())
            }`,
        }}
      />
    </Grid>
  );
};

const PositionUpdatePortal: React.FC<FundInfoComponentProps> = (props) => {
  return (
    <PortalFetchWrapper
      {...props}
      component={<PositionUpdatePortalComponent {...props} />}
    />
  );
};

export default PositionUpdatePortal;

export function MutateColumnsToIncludeEditableFields(
  columns: any[],
  transformedAuthKeyNameArr: any[],
): ColumnData[] {
  // Create a map of existing columns for quick lookup
  const columnMap = new Map(columns.map((col) => [col.id, col]));

  transformedAuthKeyNameArr.forEach((auth) => {
    if (columnMap.has(auth.key_name)) {
      // Update existing column to include editable auth
      columnMap.set(auth.key_name, {
        ...columnMap.get(auth.key_name),
        editable: auth,
      });
    } else {
      // Create a new column and add it
      columnMap.set(auth.key_name, {
        id: auth.key_name,
        editable: auth,
        filterVariant: 'text',
      });
    }
  });

  // Return updated columns as an array
  return Array.from(columnMap.values());
}

function createHedgeFundPresets(
  data: any[],
  defaultColumns: any[],
): UltraTablePreset[] {
  const map = new Map<string, string[]>();

  data.forEach((item) => {
    if (!map.has(item.raptor_tab_title)) {
      map.set(item.raptor_tab_title, []);
    }
    map.get(item.raptor_tab_title)?.push(item.key_name);
  });

  const presets: UltraTablePreset[] = [];

  map.forEach((columns, title) => {
    const combinedColumns = [
      ColumnFields.INDEX,
      ...defaultColumns.map((col) => col.column_key),
      ...columns,
    ];

    const uniqueColumns = Array.from(new Set(combinedColumns));

    presets.push({
      id: title,
      name: title,
      columns: [...uniqueColumns],
    });
  });

  return presets;
}
